import { useState, useMemo, type HTMLAttributes } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { SingleItemAutocomplete } from '~components/autocomplete/single-item-autocomplete'
import {
  useGetPortInfoPorts,
  type PortInfoPort,
} from '~hooks/queries/port-info/use-get-port-info-ports'
import { inputWidth } from '../../add-visit-flow'
import type { PortSelection } from '../../../../types'

interface PortOption {
  label: string
  port: PortInfoPort
}

function formatPortLabel(port: PortInfoPort): string {
  return `${port.PortName} - ${port.CountryName}`
}

interface PortAutocompleteProps {
  value?: PortSelection
  onOptionChange: (value: PortSelection | undefined) => void
  error?: boolean
}

export function PortAutocomplete({
  value,
  onOptionChange,
  error = false,
}: PortAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value?.portName || '')

  const shouldFetch = !value?.port

  const { data: ports, isLoading } = useGetPortInfoPorts({
    portName: shouldFetch ? inputValue : undefined,
  })

  const options: PortOption[] = useMemo(() => {
    return (
      ports?.map((port) => ({
        label: formatPortLabel(port),
        port,
      })) || []
    )
  }, [ports])

  const selectedOption = useMemo(() => {
    if (!value?.port) return null
    return {
      label: formatPortLabel(value.port),
      port: value.port,
    }
  }, [value])

  return (
    <SingleItemAutocomplete<PortOption>
      sx={{ width: inputWidth }}
      label="Port *"
      input={selectedOption?.label || ''}
      setInputValue={setInputValue}
      options={options}
      placeholder="Type 3 characters to search"
      hasError={error}
      getOptionLabel={(option: PortOption | string) =>
        typeof option === 'string' ? option : option.label
      }
      renderOption={(
        props: HTMLAttributes<HTMLLIElement> & { key: string },
        option: PortOption,
      ) => {
        const { key, ...otherProps } = props
        return (
          <Box component="li" key={key} {...otherProps}>
            <Typography variant="paragraphBold1">
              {option.port.PortName}
              <Typography component="span" variant="paragraph2">
                {' '}
                - {option.port.CountryName}
              </Typography>
            </Typography>
          </Box>
        )
      }}
      isOptionEqualToValue={(option: PortOption, val: PortOption | string) => {
        if (typeof val === 'string') return option.label === val
        return (
          option.port.PortCode === val.port.PortCode &&
          option.port.PortName === val.port.PortName
        )
      }}
      onChangeHandler={(_e, newValue) => {
        if (!newValue || typeof newValue === 'string') {
          onOptionChange(undefined)
          return
        }
        onOptionChange({
          port: newValue.port,
          portName: newValue.label,
        })
      }}
      filterOptions={(x: PortOption[]) => x}
      endAdornment={isLoading ? <CircularProgress size={20} /> : null}
    />
  )
}
